import { useState, useEffect, useCallback } from "react";
import { 
  Brain, RefreshCw, Plus, Trash2, Download, Upload, Search, 
  Check, AlertTriangle, Building2, Users, FileText, 
  ChevronDown, ChevronUp, BookOpen, Calculator, Sparkles,
  TrendingUp, DollarSign, ArrowRight, X, CloudDownload
} from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const RachelAccountingPanel = () => {
  const [loading, setLoading] = useState(true);
  const [knowledge, setKnowledge] = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); // overview, pcg, learned, classify, business, pennylane
  const [searchQuery, setSearchQuery] = useState("");
  const [classifyInput, setClassifyInput] = useState({ label: "", amount: "" });
  const [classifyResult, setClassifyResult] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState("");
  const [importing, setImporting] = useState(false);
  const [learnModal, setLearnModal] = useState(null);
  const [learnForm, setLearnForm] = useState({
    supplier_name: "",
    categorie: "",
    compte_charge: "",
    compte_tiers: "",
    type_tiers: "fournisseur"
  });
  
  // Business config state
  const [businessConfig, setBusinessConfig] = useState(null);
  const [newKeyword, setNewKeyword] = useState("");
  const [addingKeyword, setAddingKeyword] = useState(null); // "general" ou "btp"
  const [savingConfig, setSavingConfig] = useState(false);
  
  // Pennylane sync state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [pennylaneData, setPennylaneData] = useState(null);

  const loadKnowledge = useCallback(async () => {
    setLoading(true);
    try {
      const [knowledgeResp, configResp] = await Promise.all([
        authFetch(`${API}/rachel/knowledge`),
        authFetch(`${API}/rachel/business-config`)
      ]);
      
      if (knowledgeResp.ok) {
        const data = await knowledgeResp.json();
        setKnowledge(data);
      }
      
      if (configResp.ok) {
        const data = await configResp.json();
        setBusinessConfig(data.config);
      }
    } catch (e) {
      console.error("Erreur chargement connaissances Rachel:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadKnowledge();
  }, [loadKnowledge]);
  
  const handleAddKeyword = async (keywordType) => {
    if (!newKeyword.trim()) return;
    setSavingConfig(true);
    try {
      const resp = await authFetch(`${API}/rachel/business-config/keyword`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          keyword: newKeyword,
          keyword_type: keywordType === "general" ? "general_charges" : "client_attributable"
        })
      });
      if (resp.ok) {
        setNewKeyword("");
        setAddingKeyword(null);
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur ajout mot-clé:", e);
    }
    setSavingConfig(false);
  };
  
  const handleRemoveKeyword = async (keyword, keywordType) => {
    if (!window.confirm(`Supprimer "${keyword}" ?`)) return;
    try {
      const resp = await authFetch(`${API}/rachel/business-config/keyword`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          keyword: keyword,
          keyword_type: keywordType
        })
      });
      if (resp.ok) {
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur suppression mot-clé:", e);
    }
  };

  const handleClassify = async () => {
    if (!classifyInput.label.trim()) return;
    setClassifying(true);
    setClassifyResult(null);
    try {
      const resp = await authFetch(`${API}/rachel/classify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label: classifyInput.label,
          amount: classifyInput.amount ? parseFloat(classifyInput.amount) : null,
          use_ai: true
        })
      });
      if (resp.ok) {
        const data = await resp.json();
        setClassifyResult(data);
        // Si c'est une nouvelle classification, proposer d'apprendre
        if (data.needs_validation && data.classification) {
          setLearnModal(data);
          setLearnForm({
            supplier_name: data.supplier_name || "",
            categorie: data.classification?.category_label || "",
            compte_charge: data.classification?.compte_charge || "",
            compte_tiers: data.classification?.compte_tiers || data.classification?.compte_tiers_prefix || "",
            type_tiers: data.classification?.type_tiers || "fournisseur"
          });
        }
      }
    } catch (e) {
      console.error("Erreur classification:", e);
    }
    setClassifying(false);
  };

  const handleLearn = async () => {
    if (!learnForm.supplier_name || !learnForm.categorie || !learnForm.compte_charge || !learnForm.compte_tiers) {
      alert("Veuillez remplir tous les champs");
      return;
    }
    try {
      const resp = await authFetch(`${API}/rachel/learn`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(learnForm)
      });
      if (resp.ok) {
        setLearnModal(null);
        await loadKnowledge();
        alert("✅ Rachel a appris ce mapping !");
      }
    } catch (e) {
      console.error("Erreur apprentissage:", e);
    }
  };

  const handleExport = async () => {
    try {
      const resp = await authFetch(`${API}/rachel/export`);
      if (resp.ok) {
        const data = await resp.json();
        // Créer un fichier téléchargeable
        const blob = new Blob([data.data], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error("Erreur export:", e);
    }
  };

  const handleImport = async () => {
    if (!importData.trim()) return;
    setImporting(true);
    try {
      const resp = await authFetch(`${API}/rachel/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ json_data: importData, merge: true })
      });
      if (resp.ok) {
        const data = await resp.json();
        alert(`✅ Import réussi !\n- ${data.imported.suppliers} fournisseurs\n- ${data.imported.customers} clients`);
        setShowImportModal(false);
        setImportData("");
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur import:", e);
      alert("Erreur lors de l'import");
    }
    setImporting(false);
  };

  const handleDeleteLearned = async (type, name) => {
    if (!confirm(`Supprimer le mapping "${name}" ?`)) return;
    try {
      const resp = await authFetch(`${API}/rachel/learned/${type}/${encodeURIComponent(name)}`, {
        method: "DELETE"
      });
      if (resp.ok) {
        await loadKnowledge();
      }
    } catch (e) {
      console.error("Erreur suppression:", e);
    }
  };

  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  // ═══════════════════════════════════════════════════════════════
  // SYNCHRONISATION PENNYLANE
  // ═══════════════════════════════════════════════════════════════
  const handleSyncPennylane = async () => {
    setSyncing(true);
    setSyncResult(null);
    
    try {
      const resp = await authFetch(`${API}/rachel-v2/sync-pennylane`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setSyncResult(data);
        
        // Rafraîchir les données
        await loadPennylaneData();
      } else {
        const error = await resp.json();
        setSyncResult({ success: false, error: error.detail || "Erreur de synchronisation" });
      }
    } catch (e) {
      console.error("Erreur sync Pennylane:", e);
      setSyncResult({ success: false, error: e.message });
    }
    
    setSyncing(false);
  };

  const loadPennylaneData = async () => {
    try {
      const resp = await authFetch(`${API}/rachel-v2/pennylane-sync`);
      if (resp.ok) {
        const data = await resp.json();
        setPennylaneData(data);
      }
    } catch (e) {
      console.error("Erreur chargement Pennylane:", e);
    }
  };

  // Charger les données Pennylane au montage si l'onglet est actif
  useEffect(() => {
    if (activeTab === "pennylane") {
      loadPennylaneData();
    }
  }, [activeTab]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (!knowledge) {
    return (
      <div className="text-center py-12 text-slate-500">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3" />
        <p>Impossible de charger les données</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
          <Calculator className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
          <p className="text-2xl font-bold text-emerald-400">{knowledge.pcg?.total_comptes || 0}</p>
          <p className="text-xs text-slate-400">Comptes PCG</p>
        </div>
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 text-center">
          <BookOpen className="w-8 h-8 mx-auto mb-2 text-purple-400" />
          <p className="text-2xl font-bold text-purple-400">{knowledge.categories?.length || 0}</p>
          <p className="text-xs text-slate-400">Catégories</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
          <Building2 className="w-8 h-8 mx-auto mb-2 text-blue-400" />
          <p className="text-2xl font-bold text-blue-400">{knowledge.learned?.stats?.total_suppliers || 0}</p>
          <p className="text-xs text-slate-400">Fournisseurs appris</p>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
          <Users className="w-8 h-8 mx-auto mb-2 text-amber-400" />
          <p className="text-2xl font-bold text-amber-400">{knowledge.learned?.stats?.total_customers || 0}</p>
          <p className="text-xs text-slate-400">Clients appris</p>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: "overview", label: "Vue d'ensemble", icon: TrendingUp },
          { id: "pennylane", label: "Sync Pennylane", icon: CloudDownload },
          { id: "business", label: "Règles Métier", icon: Building2 },
          { id: "classify", label: "Classifier", icon: Sparkles },
          { id: "learned", label: "Mappings appris", icon: Brain },
          { id: "pcg", label: "Plan Comptable", icon: FileText },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === tab.id
                ? "bg-emerald-600 text-white"
                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
        
        {/* Boutons d'action */}
        <div className="ml-auto flex gap-2">
          <button
            onClick={handleExport}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm flex items-center gap-2"
            title="Exporter pour déploiement"
          >
            <Download className="w-4 h-4" />
            Exporter
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm flex items-center gap-2"
            title="Importer des apprentissages"
          >
            <Upload className="w-4 h-4" />
            Importer
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET SYNC PENNYLANE */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === "pennylane" && (
        <div className="space-y-6">
          {/* Bouton de synchronisation */}
          <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-orange-400 flex items-center gap-2">
                  <CloudDownload className="w-5 h-5" />
                  Synchronisation Pennylane
                </h3>
                <p className="text-sm text-slate-400 mt-1">
                  Récupère automatiquement les fournisseurs, clients, comptes comptables et catégories depuis Pennylane
                </p>
              </div>
              <button
                onClick={handleSyncPennylane}
                disabled={syncing}
                className="px-6 py-3 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 rounded-xl font-medium flex items-center gap-2"
              >
                {syncing ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <CloudDownload className="w-5 h-5" />
                )}
                {syncing ? "Synchronisation..." : "Synchroniser maintenant"}
              </button>
            </div>
            
            {/* Résultat de la synchro */}
            {syncResult && (
              <div className={`p-4 rounded-lg ${syncResult.success ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                {syncResult.success ? (
                  <div>
                    <p className="font-medium text-emerald-400 flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      {syncResult.message}
                    </p>
                    {syncResult.details && (
                      <div className="grid grid-cols-4 gap-4 mt-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-400">{syncResult.details.suppliers?.synced || 0}</p>
                          <p className="text-xs text-slate-400">Fournisseurs</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-amber-400">{syncResult.details.customers?.synced || 0}</p>
                          <p className="text-xs text-slate-400">Clients</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-emerald-400">{syncResult.details.accounts?.synced || 0}</p>
                          <p className="text-xs text-slate-400">Comptes</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-purple-400">{syncResult.details.categories?.synced || 0}</p>
                          <p className="text-xs text-slate-400">Catégories</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-red-400 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    {syncResult.error}
                  </p>
                )}
              </div>
            )}
          </div>
          
          {/* Données synchronisées */}
          {pennylaneData && pennylaneData.counts && (
            <div className="grid grid-cols-2 gap-6">
              {/* Fournisseurs */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                <h4 className="font-medium text-blue-400 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Fournisseurs Pennylane ({pennylaneData.counts.suppliers || 0})
                </h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {pennylaneData.data?.suppliers?.slice(0, 20).map((s, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-slate-900/50 rounded text-sm">
                      <span>{s.name}</span>
                      <span className="text-slate-500 font-mono text-xs">{s.account_number}</span>
                    </div>
                  ))}
                  {(pennylaneData.counts.suppliers || 0) > 20 && (
                    <p className="text-xs text-slate-500 text-center py-2">
                      + {pennylaneData.counts.suppliers - 20} autres fournisseurs
                    </p>
                  )}
                </div>
              </div>
              
              {/* Clients */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                <h4 className="font-medium text-amber-400 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Clients Pennylane ({pennylaneData.counts.customers || 0})
                </h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {pennylaneData.data?.customers?.slice(0, 20).map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-slate-900/50 rounded text-sm">
                      <span>{c.name}</span>
                      <span className="text-slate-500 font-mono text-xs">{c.account_number}</span>
                    </div>
                  ))}
                  {(pennylaneData.counts.customers || 0) > 20 && (
                    <p className="text-xs text-slate-500 text-center py-2">
                      + {pennylaneData.counts.customers - 20} autres clients
                    </p>
                  )}
                </div>
              </div>
              
              {/* Plan comptable */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                <h4 className="font-medium text-emerald-400 mb-3 flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  Plan Comptable ({pennylaneData.counts.accounts || 0})
                </h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {pennylaneData.data?.accounts?.slice(0, 20).map((a, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-slate-900/50 rounded text-sm">
                      <span className="font-mono text-emerald-400">{a.number}</span>
                      <span className="text-slate-300 truncate ml-2">{a.label}</span>
                    </div>
                  ))}
                  {(pennylaneData.counts.accounts || 0) > 20 && (
                    <p className="text-xs text-slate-500 text-center py-2">
                      + {pennylaneData.counts.accounts - 20} autres comptes
                    </p>
                  )}
                </div>
              </div>
              
              {/* Catégories */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                <h4 className="font-medium text-purple-400 mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Catégories ({pennylaneData.counts.categories || 0})
                </h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {pennylaneData.data?.categories?.slice(0, 20).map((cat, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-slate-900/50 rounded text-sm">
                      <span>{cat.name}</span>
                      <span className="text-slate-500 text-xs">{cat.group}</span>
                    </div>
                  ))}
                  {(pennylaneData.counts.categories || 0) > 20 && (
                    <p className="text-xs text-slate-500 text-center py-2">
                      + {pennylaneData.counts.categories - 20} autres catégories
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Message si pas de données */}
          {(!pennylaneData || !pennylaneData.counts || Object.values(pennylaneData.counts).every(v => v === 0)) && !syncing && (
            <div className="text-center py-12 text-slate-500">
              <CloudDownload className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune donnée synchronisée</p>
              <p className="text-sm mt-2">Cliquez sur "Synchroniser maintenant" pour récupérer les données Pennylane</p>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ONGLET RÈGLES MÉTIER */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {activeTab === "business" && businessConfig && (
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-6">
            <h3 className="font-semibold text-amber-400 mb-2 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Configuration Métier - {businessConfig.company_type || "BTP"}
            </h3>
            <p className="text-slate-400 text-sm">
              Ces règles déterminent si une dépense peut être attribuée à un client/chantier ou est une charge de fonctionnement.
              Personnalisez-les selon votre secteur d'activité.
            </p>
          </div>
          
          {/* Fournisseurs BTP (attribuables) */}
          <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-emerald-500/20 flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-emerald-400 flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Fournisseurs Matériaux & Sous-traitants
                </h4>
                <p className="text-xs text-slate-400 mt-1">
                  Ces mots-clés identifient les dépenses ATTRIBUABLES à un chantier/client
                </p>
              </div>
              <button
                onClick={() => setAddingKeyword(addingKeyword === "btp" ? null : "btp")}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Ajouter
              </button>
            </div>
            
            {addingKeyword === "btp" && (
              <div className="p-4 bg-emerald-500/10 border-b border-emerald-500/20 flex gap-2">
                <input
                  type="text"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="Ex: POINT P, CEDEO..."
                  className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                  onKeyDown={(e) => e.key === "Enter" && handleAddKeyword("btp")}
                />
                <button
                  onClick={() => handleAddKeyword("btp")}
                  disabled={savingConfig || !newKeyword.trim()}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg text-sm"
                >
                  {savingConfig ? "..." : "Ajouter"}
                </button>
                <button
                  onClick={() => { setAddingKeyword(null); setNewKeyword(""); }}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            
            <div className="p-4 flex flex-wrap gap-2 max-h-60 overflow-y-auto">
              {(businessConfig.client_attributable_keywords || []).map((kw, i) => (
                <span
                  key={i}
                  className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm flex items-center gap-2 group"
                >
                  {kw}
                  <button
                    onClick={() => handleRemoveKeyword(kw, "client_attributable")}
                    className="opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {(!businessConfig.client_attributable_keywords || businessConfig.client_attributable_keywords.length === 0) && (
                <p className="text-slate-500 text-sm">Aucun mot-clé configuré</p>
              )}
            </div>
          </div>
          
          {/* Charges Générales (non attribuables) */}
          <div className="bg-red-500/5 border border-red-500/30 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-red-500/20 flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-red-400 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Charges Générales (Fonctionnement)
                </h4>
                <p className="text-xs text-slate-400 mt-1">
                  Ces mots-clés identifient les dépenses NON ATTRIBUABLES (frais de fonctionnement)
                </p>
              </div>
              <button
                onClick={() => setAddingKeyword(addingKeyword === "general" ? null : "general")}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Ajouter
              </button>
            </div>
            
            {addingKeyword === "general" && (
              <div className="p-4 bg-red-500/10 border-b border-red-500/20 flex gap-2">
                <input
                  type="text"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="Ex: OVH, AMAZON, TOTAL..."
                  className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                  onKeyDown={(e) => e.key === "Enter" && handleAddKeyword("general")}
                />
                <button
                  onClick={() => handleAddKeyword("general")}
                  disabled={savingConfig || !newKeyword.trim()}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg text-sm"
                >
                  {savingConfig ? "..." : "Ajouter"}
                </button>
                <button
                  onClick={() => { setAddingKeyword(null); setNewKeyword(""); }}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            
            <div className="p-4 flex flex-wrap gap-2 max-h-60 overflow-y-auto">
              {(businessConfig.general_charges_keywords || []).map((kw, i) => (
                <span
                  key={i}
                  className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm flex items-center gap-2 group"
                >
                  {kw}
                  <button
                    onClick={() => handleRemoveKeyword(kw, "general_charges")}
                    className="opacity-0 group-hover:opacity-100 hover:text-white transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {(!businessConfig.general_charges_keywords || businessConfig.general_charges_keywords.length === 0) && (
                <p className="text-slate-500 text-sm">Aucun mot-clé configuré</p>
              )}
            </div>
          </div>
          
          {/* Info */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <h4 className="font-semibold text-blue-400 mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Comment ça fonctionne ?
            </h4>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Dans <strong>Attribution en masse</strong>, Rachel ignore automatiquement les charges générales</li>
              <li>• Seules les dépenses correspondant aux fournisseurs BTP sont proposées pour attribution</li>
              <li>• Vous pouvez personnaliser ces listes selon votre secteur d'activité</li>
              <li>• Ces règles sont stockées en base de données (persistantes)</li>
            </ul>
          </div>
        </div>
      )}

      {/* Contenu des onglets */}
      {activeTab === "overview" && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-6">
            <h3 className="font-semibold text-emerald-400 mb-4 flex items-center gap-2">
              <Brain className="w-5 h-5" />
              Rachel Levy - Experte Comptable IA
            </h3>
            <p className="text-slate-300 mb-4">
              Rachel peut classifier automatiquement vos transactions bancaires et associer chaque dépense au bon compte comptable.
              Elle apprend de vos validations pour s'améliorer continuellement.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="font-medium text-slate-200 mb-2">🎯 Ce que Rachel sait faire :</h4>
                <ul className="text-sm text-slate-400 space-y-1">
                  <li>• Identifier les fournisseurs depuis les libellés bancaires</li>
                  <li>• Attribuer le compte de charge approprié (classe 6)</li>
                  <li>• Créer des comptes tiers uniques (401XXX, 411XXX)</li>
                  <li>• Extraire les n° de facture et noms de chantiers</li>
                  <li>• Apprendre de vos corrections</li>
                </ul>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="font-medium text-slate-200 mb-2">📊 Catégories maîtrisées :</h4>
                <div className="flex flex-wrap gap-2">
                  {knowledge.categories?.slice(0, 8).map(cat => (
                    <span key={cat.id} className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300">
                      {cat.label}
                    </span>
                  ))}
                  {knowledge.categories?.length > 8 && (
                    <span className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-400">
                      +{knowledge.categories.length - 8} autres
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Info persistance */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <h4 className="font-medium text-amber-400 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Persistance des données
            </h4>
            <p className="text-sm text-slate-400">
              Avant un déploiement, pensez à <strong className="text-amber-300">exporter</strong> les apprentissages de Rachel. 
              Vous pourrez ensuite les <strong className="text-amber-300">importer</strong> dans le nouvel environnement pour conserver sa mémoire.
            </p>
          </div>
        </div>
      )}

      {activeTab === "classify" && (
        <div className="space-y-4">
          {/* Formulaire de classification */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              Tester la classification
            </h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="col-span-2">
                <label className="block text-sm text-slate-400 mb-1">Libellé bancaire</label>
                <input
                  type="text"
                  value={classifyInput.label}
                  onChange={(e) => setClassifyInput({ ...classifyInput, label: e.target.value })}
                  placeholder="Ex: VIR LEROY MERLIN MARSEILLE FA2024-001"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3"
                  onKeyPress={(e) => e.key === "Enter" && handleClassify()}
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Montant (€)</label>
                <input
                  type="number"
                  value={classifyInput.amount}
                  onChange={(e) => setClassifyInput({ ...classifyInput, amount: e.target.value })}
                  placeholder="-234.50"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3"
                />
              </div>
            </div>
            <button
              onClick={handleClassify}
              disabled={classifying || !classifyInput.label.trim()}
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg flex items-center gap-2"
            >
              {classifying ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Brain className="w-4 h-4" />
              )}
              Classifier avec Rachel
            </button>
          </div>

          {/* Résultat */}
          {classifyResult && (
            <div className={`border rounded-xl p-6 ${
              classifyResult.needs_validation 
                ? "bg-amber-500/10 border-amber-500/30" 
                : "bg-emerald-500/10 border-emerald-500/30"
            }`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="font-semibold flex items-center gap-2">
                    {classifyResult.needs_validation ? (
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    ) : (
                      <Check className="w-5 h-5 text-emerald-400" />
                    )}
                    {classifyResult.needs_validation ? "À valider" : "Classification confirmée"}
                  </h4>
                  <p className="text-sm text-slate-400">
                    Source: {classifyResult.classification?.source || "inconnu"}
                    {classifyResult.classification?.confidence && (
                      <span className="ml-2">
                        (Confiance: {Math.round(classifyResult.classification.confidence * 100)}%)
                      </span>
                    )}
                  </p>
                </div>
                {classifyResult.needs_validation && classifyResult.classification && (
                  <button
                    onClick={() => {
                      setLearnModal(classifyResult);
                      setLearnForm({
                        supplier_name: classifyResult.supplier_name || "",
                        categorie: classifyResult.classification?.category_label || "",
                        compte_charge: classifyResult.classification?.compte_charge || "",
                        compte_tiers: classifyResult.classification?.compte_tiers || classifyResult.classification?.compte_tiers_prefix || "",
                        type_tiers: classifyResult.classification?.type_tiers || "fournisseur"
                      });
                    }}
                    className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm flex items-center gap-1"
                  >
                    <Check className="w-4 h-4" />
                    Valider & Apprendre
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Fournisseur détecté</p>
                  <p className="font-medium text-blue-400">{classifyResult.supplier_name || "Non détecté"}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Catégorie</p>
                  <p className="font-medium text-purple-400">
                    {classifyResult.classification?.category_label || "Non classifié"}
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Compte de charge</p>
                  <p className="font-medium text-emerald-400">
                    {classifyResult.classification?.compte_charge || "-"}
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Compte tiers</p>
                  <p className="font-medium text-amber-400">
                    {classifyResult.classification?.compte_tiers || classifyResult.classification?.compte_tiers_prefix || "-"}
                  </p>
                </div>
              </div>

              {/* Métadonnées extraites */}
              {classifyResult.metadata && Object.keys(classifyResult.metadata).length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <p className="text-xs text-slate-500 mb-2">Métadonnées extraites :</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(classifyResult.metadata).map(([key, value]) => (
                      <span key={key} className="px-2 py-1 bg-slate-700 rounded text-xs">
                        {key}: <span className="text-blue-300">{value}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === "learned" && (
        <div className="space-y-4">
          {/* Fournisseurs appris */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-400" />
              Fournisseurs appris ({Object.keys(knowledge.learned?.suppliers || {}).length})
            </h3>
            
            {Object.keys(knowledge.learned?.suppliers || {}).length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Brain className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun fournisseur appris</p>
                <p className="text-sm">Utilisez l'onglet "Classifier" pour enseigner à Rachel</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {Object.entries(knowledge.learned?.suppliers || {}).map(([name, mapping]) => (
                  <div 
                    key={name}
                    className="flex items-center gap-4 bg-slate-900/50 rounded-lg p-3 hover:bg-slate-900"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-slate-200">{mapping.original_name || name}</p>
                      <p className="text-sm text-slate-400">
                        {mapping.categorie} 
                        <span className="mx-2">•</span>
                        <span className="text-emerald-400">{mapping.compte_charge}</span>
                        <ArrowRight className="w-3 h-3 inline mx-1" />
                        <span className="text-amber-400">{mapping.compte_tiers}</span>
                      </p>
                    </div>
                    <div className="text-right text-xs text-slate-500">
                      <p>Appris le {mapping.learned_at?.split("T")[0]}</p>
                      <p>par {mapping.learned_by}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteLearned("supplier", name)}
                      className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Clients appris */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-400" />
              Clients appris ({Object.keys(knowledge.learned?.customers || {}).length})
            </h3>
            
            {Object.keys(knowledge.learned?.customers || {}).length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun client appris</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {Object.entries(knowledge.learned?.customers || {}).map(([name, mapping]) => (
                  <div 
                    key={name}
                    className="flex items-center gap-4 bg-slate-900/50 rounded-lg p-3"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-slate-200">{mapping.original_name || name}</p>
                      <p className="text-sm text-slate-400">
                        Compte: <span className="text-amber-400">{mapping.compte_tiers}</span>
                        {mapping.chantier && (
                          <span className="ml-2">• Chantier: {mapping.chantier}</span>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteLearned("customer", name)}
                      className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "pcg" && (
        <div className="space-y-4">
          {/* Recherche */}
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un compte (numéro ou libellé)..."
              className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-3"
            />
          </div>

          {/* Comptes par classe */}
          <div className="space-y-3">
            {Object.entries(knowledge.pcg?.by_class || {}).map(([classe, info]) => (
              <div 
                key={classe}
                className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleCategory(classe)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-800"
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${
                      classe === "4" ? "bg-blue-500/20 text-blue-400" :
                      classe === "5" ? "bg-green-500/20 text-green-400" :
                      classe === "6" ? "bg-red-500/20 text-red-400" :
                      classe === "7" ? "bg-purple-500/20 text-purple-400" :
                      "bg-slate-600 text-slate-300"
                    }`}>
                      {classe}
                    </span>
                    <div className="text-left">
                      <p className="font-medium">{info.label}</p>
                      <p className="text-sm text-slate-400">{info.count} comptes</p>
                    </div>
                  </div>
                  {expandedCategories[classe] ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
              </div>
            ))}
          </div>
          
          <p className="text-sm text-slate-500 text-center">
            Le Plan Comptable Général contient {knowledge.pcg?.total_comptes} comptes adaptés au BTP
          </p>
        </div>
      )}

      {/* Modal d'apprentissage */}
      {learnModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h3 className="font-semibold flex items-center gap-2">
                <Brain className="w-5 h-5 text-emerald-400" />
                Valider et apprendre
              </h3>
              <button onClick={() => setLearnModal(null)} className="p-2 hover:bg-slate-800 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Nom du fournisseur</label>
                <input
                  type="text"
                  value={learnForm.supplier_name}
                  onChange={(e) => setLearnForm({ ...learnForm, supplier_name: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Catégorie</label>
                <input
                  type="text"
                  value={learnForm.categorie}
                  onChange={(e) => setLearnForm({ ...learnForm, categorie: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2"
                  placeholder="Ex: Fournisseur Matériaux, Sous-traitant Plomberie..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Compte de charge</label>
                  <input
                    type="text"
                    value={learnForm.compte_charge}
                    onChange={(e) => setLearnForm({ ...learnForm, compte_charge: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2"
                    placeholder="607000"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Compte tiers</label>
                  <input
                    type="text"
                    value={learnForm.compte_tiers}
                    onChange={(e) => setLearnForm({ ...learnForm, compte_tiers: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2"
                    placeholder="401LEROY"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Type</label>
                <select
                  value={learnForm.type_tiers}
                  onChange={(e) => setLearnForm({ ...learnForm, type_tiers: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2"
                >
                  <option value="fournisseur">Fournisseur</option>
                  <option value="sous_traitant">Sous-traitant</option>
                  <option value="charges_sociales">Charges sociales</option>
                  <option value="impots">Impôts et taxes</option>
                  <option value="frais_generaux">Frais généraux</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-slate-700">
              <button
                onClick={() => setLearnModal(null)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleLearn}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                Rachel apprend !
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'import */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h3 className="font-semibold flex items-center gap-2">
                <Upload className="w-5 h-5 text-purple-400" />
                Importer des apprentissages
              </h3>
              <button onClick={() => setShowImportModal(false)} className="p-2 hover:bg-slate-800 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-400 mb-4">
                Collez le contenu JSON exporté précédemment pour restaurer les apprentissages de Rachel.
              </p>
              <textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder='{"suppliers": {...}, "customers": {...}}'
                className="w-full h-64 bg-slate-800 border border-slate-700 rounded-lg p-4 font-mono text-sm"
              />
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-slate-700">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleImport}
                disabled={importing || !importData.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-lg flex items-center gap-2"
              >
                {importing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Importer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RachelAccountingPanel;
